import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, ZoomControl } from 'react-leaflet';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Thermometer, Wind, CloudRain, Cloud, Gauge, Droplets, CheckIcon, XIcon } from 'lucide-react';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { cn } from '../components/ui/Button';
import { TemperatureLegend } from '../components/map/TemperatureLegend';
import { TimelineControls } from '../components/map/TimelineControls';
import { useTheme } from '../hooks/useTheme';
import 'leaflet/dist/leaflet.css';
export interface OverlayType {
  id: string;
  name: string;
  unit: string;
  icon: React.ElementType;
  gradient: string;
  labels: string[];
  simulation: string;
}
export const OVERLAY_OPTIONS: OverlayType[] = [{
  id: 'temperature',
  name: 'Temperature 2m',
  unit: '°C',
  icon: Thermometer,
  gradient: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e, #eab308, #f97316, #ef4444, #ec4899)',
  labels: ['-50', '-30', '-10', '10', '30', '50'],
  simulation: `
      radial-gradient(ellipse 600px 400px at 25% 35%, rgba(239,68,68,0.55) 0%, rgba(234,179,8,0.3) 40%, transparent 70%),
      radial-gradient(ellipse 500px 350px at 70% 25%, rgba(59,130,246,0.5) 0%, rgba(6,182,212,0.25) 45%, transparent 70%),
      radial-gradient(ellipse 450px 300px at 50% 60%, rgba(234,179,8,0.45) 0%, rgba(34,197,94,0.25) 40%, transparent 65%),
      radial-gradient(ellipse 350px 250px at 15% 70%, rgba(6,182,212,0.4) 0%, rgba(59,130,246,0.2) 50%, transparent 70%),
      radial-gradient(ellipse 400px 280px at 80% 65%, rgba(249,115,22,0.45) 0%, rgba(239,68,68,0.2) 45%, transparent 70%),
      radial-gradient(ellipse 300px 200px at 40% 20%, rgba(34,197,94,0.35) 0%, rgba(6,182,212,0.15) 50%, transparent 70%),
      radial-gradient(ellipse 550px 380px at 60% 80%, rgba(59,130,246,0.35) 0%, rgba(34,197,94,0.15) 40%, transparent 65%)
    `
}, {
  id: 'wind',
  name: 'Wind Speed 100m',
  unit: 'm/s',
  icon: Wind,
  gradient: 'linear-gradient(to right, #22c55e, #84cc16, #eab308, #f97316, #ef4444, #a855f7)',
  labels: ['0', '5', '10', '15', '25', '40'],
  simulation: `
      radial-gradient(ellipse 700px 500px at 30% 30%, rgba(168,85,247,0.5) 0%, rgba(239,68,68,0.3) 35%, transparent 65%),
      radial-gradient(ellipse 400px 300px at 65% 45%, rgba(234,179,8,0.4) 0%, rgba(132,204,22,0.2) 45%, transparent 70%),
      radial-gradient(ellipse 500px 350px at 20% 65%, rgba(249,115,22,0.45) 0%, rgba(234,179,8,0.2) 40%, transparent 65%),
      radial-gradient(ellipse 350px 250px at 75% 20%, rgba(34,197,94,0.35) 0%, rgba(132,204,22,0.15) 50%, transparent 70%),
      radial-gradient(ellipse 600px 400px at 50% 75%, rgba(132,204,22,0.3) 0%, rgba(34,197,94,0.15) 40%, transparent 65%),
      radial-gradient(ellipse 450px 320px at 85% 70%, rgba(239,68,68,0.4) 0%, rgba(249,115,22,0.2) 45%, transparent 70%)
    `
}, {
  id: 'precipitation',
  name: 'Precipitation',
  unit: 'mm/h',
  icon: CloudRain,
  gradient: 'linear-gradient(to right, rgba(191,219,254,0.2), #93c5fd, #3b82f6, #4f46e5, #7c3aed)',
  labels: ['0', '0.5', '2', '5', '10', '25'],
  simulation: `
      radial-gradient(ellipse 350px 250px at 35% 40%, rgba(79,70,229,0.5) 0%, rgba(59,130,246,0.3) 40%, transparent 65%),
      radial-gradient(ellipse 250px 180px at 55% 30%, rgba(59,130,246,0.45) 0%, rgba(147,197,253,0.2) 45%, transparent 70%),
      radial-gradient(ellipse 400px 280px at 25% 60%, rgba(124,58,237,0.4) 0%, rgba(79,70,229,0.2) 40%, transparent 65%),
      radial-gradient(ellipse 200px 150px at 70% 55%, rgba(59,130,246,0.35) 0%, rgba(147,197,253,0.15) 50%, transparent 70%),
      radial-gradient(ellipse 300px 220px at 45% 70%, rgba(79,70,229,0.35) 0%, rgba(59,130,246,0.15) 40%, transparent 65%)
    `
}, {
  id: 'cloud',
  name: 'Cloud Cover',
  unit: '%',
  icon: Cloud,
  gradient: 'linear-gradient(to right, rgba(255,255,255,0.05), rgba(203,213,225,0.4), rgba(148,163,184,0.6), rgba(100,116,139,0.8), rgba(71,85,105,0.9))',
  labels: ['0', '20', '40', '60', '80', '100'],
  simulation: `
      radial-gradient(ellipse 600px 450px at 30% 35%, rgba(148,163,184,0.55) 0%, rgba(203,213,225,0.3) 40%, transparent 65%),
      radial-gradient(ellipse 500px 350px at 60% 50%, rgba(100,116,139,0.5) 0%, rgba(148,163,184,0.25) 45%, transparent 70%),
      radial-gradient(ellipse 400px 280px at 45% 25%, rgba(71,85,105,0.45) 0%, rgba(100,116,139,0.2) 40%, transparent 65%),
      radial-gradient(ellipse 350px 250px at 75% 70%, rgba(203,213,225,0.4) 0%, rgba(226,232,240,0.15) 50%, transparent 70%),
      radial-gradient(ellipse 450px 320px at 15% 65%, rgba(148,163,184,0.35) 0%, rgba(203,213,225,0.15) 40%, transparent 65%)
    `
}, {
  id: 'pressure',
  name: 'Pressure (MSLP)',
  unit: 'hPa',
  icon: Gauge,
  gradient: 'linear-gradient(to right, #a855f7, #3b82f6, #22c55e, #eab308, #ef4444)',
  labels: ['960', '980', '1000', '1013', '1030', '1050'],
  simulation: `
      radial-gradient(ellipse 500px 400px at 35% 40%, rgba(168,85,247,0.5) 0%, rgba(59,130,246,0.3) 40%, transparent 65%),
      radial-gradient(ellipse 450px 320px at 65% 30%, rgba(239,68,68,0.45) 0%, rgba(234,179,8,0.25) 45%, transparent 70%),
      radial-gradient(ellipse 400px 280px at 25% 65%, rgba(59,130,246,0.4) 0%, rgba(34,197,94,0.2) 40%, transparent 65%),
      radial-gradient(ellipse 350px 250px at 80% 60%, rgba(234,179,8,0.4) 0%, rgba(34,197,94,0.2) 50%, transparent 70%),
      radial-gradient(ellipse 550px 380px at 50% 20%, rgba(34,197,94,0.35) 0%, rgba(59,130,246,0.15) 40%, transparent 65%)
    `
}, {
  id: 'humidity',
  name: 'Relative Humidity',
  unit: '%',
  icon: Droplets,
  gradient: 'linear-gradient(to right, #92400e, #ca8a04, #22c55e, #14b8a6, #3b82f6)',
  labels: ['0', '20', '40', '60', '80', '100'],
  simulation: `
      radial-gradient(ellipse 500px 380px at 30% 35%, rgba(59,130,246,0.5) 0%, rgba(20,184,166,0.3) 40%, transparent 65%),
      radial-gradient(ellipse 400px 300px at 65% 45%, rgba(34,197,94,0.45) 0%, rgba(20,184,166,0.2) 45%, transparent 70%),
      radial-gradient(ellipse 450px 320px at 20% 60%, rgba(20,184,166,0.4) 0%, rgba(34,197,94,0.2) 40%, transparent 65%),
      radial-gradient(ellipse 350px 250px at 75% 25%, rgba(146,64,14,0.35) 0%, rgba(202,138,4,0.15) 50%, transparent 70%),
      radial-gradient(ellipse 500px 350px at 50% 75%, rgba(34,197,94,0.35) 0%, rgba(59,130,246,0.15) 40%, transparent 65%)
    `
}];
export function MapView() {
  const {
    theme
  } = useTheme();
  const isLight = theme === 'light';
  const [activeOverlay, setActiveOverlay] = useState<string | null>('temperature');
  const [showOverlayPanel, setShowOverlayPanel] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (showOverlayPanel && panelRef.current && !panelRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setShowOverlayPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showOverlayPanel]);
  const currentOverlay = OVERLAY_OPTIONS.find(o => o.id === activeOverlay);
  return <div className="jua-page-bg relative h-[calc(100vh-64px)] w-full overflow-hidden group" data-id="element-3894">
      {/* Map */}
      <div className="h-full w-full" style={isLight ? undefined : {
      filter: 'brightness(0.55) saturate(1.3) contrast(1.1)'
    }} data-id="element-3895">
        <MapContainer center={[20, 0]} zoom={3} className={cn('h-full w-full z-0', isLight ? 'bg-[#f2efe9]' : 'bg-[#2b3040]')} zoomControl={false} attributionControl={false} data-id="element-3896">
          <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>' data-id="element-3897" />
          <ZoomControl position="topright" data-id="element-3898" />
        </MapContainer>
      </div>

      {/* Weather Overlay Simulation */}
      {currentOverlay && <div className="absolute inset-0 pointer-events-none z-[1] transition-opacity duration-700" style={{
      opacity: isLight ? 0.45 : 0.55,
      background: currentOverlay.simulation,
      mixBlendMode: isLight ? 'multiply' : 'screen'
    }} data-id="element-3899" />}

      {/* Search Overlay */}
      <div className="absolute top-6 left-6 z-[1000] w-[320px]" data-id="element-3900">
        <Input placeholder="Search for places..." className={cn('h-11 shadow-xl backdrop-blur', isLight ? 'bg-white/90 border-slate-200 text-slate-900 placeholder:text-slate-400' : 'bg-[#1E293B]/90 border-slate-700/50 text-white placeholder:text-slate-400')} data-id="element-3901" />
      </div>

      {/* Layers Toggle + Panel */}
      <div className="absolute top-6 right-6 z-[1000] mr-12" ref={buttonRef} data-id="element-3902">
        <Button size="icon" onClick={() => setShowOverlayPanel(!showOverlayPanel)} className={cn('rounded-full border shadow-xl transition-all', showOverlayPanel ? 'bg-[#EC4899]/20 border-[#EC4899]/40 text-[#EC4899] hover:bg-[#EC4899]/30' : isLight ? 'bg-white/40 border-white/60 hover:bg-white/60 text-slate-700' : 'bg-white/[0.08] border-white/[0.12] hover:bg-white/[0.14] text-white/80')} style={{
        backdropFilter: 'blur(16px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(16px) saturate(1.4)'
      }} data-id="element-3903">
          <Layers size={20} data-id="element-3904" />
        </Button>

        {/* Overlay Selector Panel */}
        <AnimatePresence data-id="element-3905">
          {showOverlayPanel && <motion.div ref={panelRef} initial={{
          opacity: 0,
          y: -8,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -8,
          scale: 0.96
        }} transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30
        }} className={cn('absolute top-14 right-0 w-[260px] rounded-xl border shadow-2xl overflow-hidden', isLight ? 'bg-white/95 border-slate-200 shadow-slate-300/40' : 'bg-[#0F172A]/95 border-white/[0.08] shadow-black/50')} style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }} data-id="element-3906">
              <div className={cn('px-3.5 py-2.5 border-b', isLight ? 'border-slate-100' : 'border-white/[0.06]')} data-id="element-3907">
                <span className={cn('text-xs font-semibold uppercase tracking-wider', isLight ? 'text-slate-500' : 'text-slate-400')} data-id="element-3908">
                  Weather Overlays
                </span>
              </div>

              <div className="p-1.5 space-y-0.5" data-id="element-3909">
                {OVERLAY_OPTIONS.map(overlay => {
              const Icon = overlay.icon;
              const isActive = activeOverlay === overlay.id;
              return <button key={overlay.id} onClick={() => {
                setActiveOverlay(isActive ? null : overlay.id);
              }} className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all', isActive ? isLight ? 'bg-[#EC4899]/10 text-[#EC4899]' : 'bg-[#EC4899]/15 text-[#EC4899]' : isLight ? 'text-slate-700 hover:bg-slate-50' : 'text-slate-300 hover:bg-white/[0.05]')} data-id="element-3910">
                      <Icon size={16} className={cn('flex-shrink-0', isActive ? 'text-[#EC4899]' : isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-3911" />
                      <span className="flex-1 text-sm font-medium" data-id="element-3912">
                        {overlay.name}
                      </span>
                      <div className="flex items-center gap-2" data-id="element-3913">
                        <div className="w-10 h-2 rounded-full flex-shrink-0" style={{
                    background: overlay.gradient
                  }} data-id="element-3914" />
                        {isActive && <CheckIcon size={14} className="text-[#EC4899] flex-shrink-0" data-id="element-3915" />}
                      </div>
                    </button>;
            })}

                {/* Turn off overlays */}
                <button onClick={() => setActiveOverlay(null)} className={cn('w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all border-t mt-1 pt-2.5', isLight ? 'border-slate-100' : 'border-white/[0.06]', activeOverlay === null ? isLight ? 'bg-slate-100 text-slate-800' : 'bg-white/[0.08] text-white' : isLight ? 'text-slate-500 hover:bg-slate-50' : 'text-slate-400 hover:bg-white/[0.05]')} data-id="element-3916">
                  <XIcon size={16} className={cn('flex-shrink-0', activeOverlay === null ? isLight ? 'text-slate-600' : 'text-white' : isLight ? 'text-slate-400' : 'text-slate-500')} data-id="element-3917" />
                  <span className="flex-1 text-sm font-medium" data-id="element-3918">No overlay</span>
                  {activeOverlay === null && <CheckIcon size={14} className={cn('flex-shrink-0', isLight ? 'text-slate-600' : 'text-white')} data-id="element-3919" />}
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Bottom Controls */}
      {currentOverlay && <TemperatureLegend overlay={currentOverlay} data-id="element-3920" />}
      <TimelineControls data-id="element-3921" />
    </div>;
}